import { API_BASE_URL, ApiError } from './client';
import { ComplianceRefusal } from './conversations';

export interface UnitSummary {
  id: string;
  name: string;
  unitType: string;
  subgroupLabelPlural: string;
}

export interface EventDetail {
  id: string;
  title: string;
  description: string | null;
  locationName: string | null;
  locationAddress: string | null;
  /** Instants. Never rendered without the zone below — see lib/datetime. */
  startsAt: string;
  endsAt: string;
  departsAt: string | null;
  returnsAt: string | null;
  /** IANA zone, already resolved server-side (event override, else unit). */
  timeZoneId: string;
  status: string;
  conversationId: string | null;
  /** The unit's word for what kind of event this is. Null is ordinary — not every event is filed. */
  eventTypeId: string | null;
  eventTypeName: string | null;
  /**
   * The swatch this event reads in, as a key. Never null: an untyped event lands on the neutral,
   * so the calendar has no ragged row. Read live off the type rather than copied onto the event —
   * a colour is a label, and recolouring a kind means every event of it, past ones included.
   */
  eventTypeColorKey: string;
  /**
   * Why the event was called off, and when. Both null while it is still on. The reason renders
   * beside the cancelled status rather than behind it — "Cancelled" alone sends a family into the
   * thread to find out whether it is the rain or the drivers.
   */
  cancellationReason: string | null;
  cancelledAt: string | null;
  /**
   * Which of the four coordination questions this event asks. The docket omits a section whose
   * flag is false, and the edit form hydrates its checkboxes from these.
   */
  requiresRsvp: boolean;
  requiresForms: boolean;
  requiresPacking: boolean;
  requiresTravel: boolean;
  requiresProgram: boolean;
  /**
   * What this particular gathering is about, in one line -- "Knots and lashings". Rendered
   * as the event's subtitle. Null until an officer writes one, which is where every event
   * starts.
   *
   * Deliberately not `description`: a description says what the event is and is usually the
   * same for every event of its kind, while a theme says what is happening at it this time.
   * A weekly meeting is fifty-two events with one description and fifty-two themes.
   */
  theme: string | null;
  /**
   * The program itself, plain text. It arrives on the event rather than behind a route of
   * its own, so the docket's program panel opens with no fetch and the resting page stays at
   * one docket call plus this one.
   *
   * **Rendered with `white-space: pre-wrap` and nothing else.** No markdown, no linkifying:
   * it is not curated text, so it never reaches `CuratedText` and a pasted URL is literal
   * characters. That is the architecture rule, not an omission.
   */
  programPlan: string | null;
  /** When it was last written and by whom. Both null until somebody has written one. */
  programUpdatedAt: string | null;
  programUpdatedBy: string | null;
  unit: UnitSummary;
}

export interface EventSummary {
  id: string;
  title: string;
  locationName: string | null;
  startsAt: string;
  departsAt: string | null;
  timeZoneId: string;
  status: string;
  conversationId: string | null;
  eventTypeName: string | null;
  /** The row's swatch, as a key. Never null — see `EventDetail.eventTypeColorKey`. */
  eventTypeColorKey: string;
  /** Why it is off, on the calendar row itself. Null while the event is still on. */
  cancellationReason: string | null;
  /**
   * Messages in this event's thread I have not been shown. Zero for a thread I cannot read at
   * all — an event thread can be adults-only, and a count would itself be a disclosure.
   */
  unreadCount: number;
  /**
   * Which unit's calendar this row is from. On every row, but it only earns its place on a
   * parent's list, which spans every unit their children are in.
   */
  unitId: string;
  unitName: string;
  /**
   * `yyyy-MM`, computed server-side in the **event's own zone** rather than the reader's — a
   * Denver campout on the 31st is filed under Denver's month wherever it is read. Never derived
   * here: a second implementation of which month an instant falls in disagrees with the server's
   * only at boundaries, which is the one place it matters and the last place anybody looks.
   */
  month: string;
  /**
   * What this one is about, as the row's subtitle. Null is ordinary -- most events on a
   * calendar have not been planned yet, and a row with nothing under it is the right
   * rendering of that.
   */
  theme: string | null;
}

/**
 * One month-shaped page of a unit's calendar — TROOP-123.
 *
 * `anchor` is the month the window actually starts at, which is not always the one asked for: a
 * month holding nothing falls back to the nearest that does, so the back and forward controls can
 * never land on an empty page. `months` is every month with an event in it, which is what those
 * controls step through. A null anchor means the unit has no events at all.
 *
 * The window is whole months and can be far larger than the ~25 it aims for — the month that
 * reaches the target comes back entire, because a reader scrolling August expects August.
 */
export interface EventCalendarPage {
  anchor: string | null;
  months: { month: string; count: number }[];
  events: EventSummary[];
}

/**
 * What the events page opens on — TROOP-213.
 *
 * The calendar above is right and was the wrong thing to *land* on: it takes months whole, so on
 * the 4th of the month the top of the page is the 1st, and a reader scrolls through their own
 * history to reach the thing they came for.
 *
 * **Two groups, neither capped.** The bound is a time window rather than a count — deliberately
 * not `DigestSelection`'s per-kind rule, which exists to solve a hard cap an email has and a page
 * does not. See `EventAgendaWindow` on the server for the whole argument, including why these are
 * in date order and never grouped by kind.
 *
 * The two day counts say **how far the server looked**, so a screen saying "nothing else coming
 * up" can say over what — and so no copy of the rule lives here.
 */
export interface EventAgendaPage {
  /** Still to come, earliest first. `EndsAt >= now`, so an event under way is here. */
  upcoming: EventSummary[];
  /** Finished recently, most recent first — the trip whose gear and photographs are still live. */
  justFinished: EventSummary[];
  looksAheadDays: number;
  looksBackDays: number;
}

/**
 * What a typed query found — TROOP-214.
 *
 * **Two groups, and the group is what says which side of today a row is on.** Ordered outwards
 * from today in both directions: soonest first going forwards, most recent first going back,
 * because *when did we last run the klondike* is answered by the first row of `past`.
 *
 * **Capped, and it says so.** The agenda is bounded by a time window it declares; a search has no
 * time bound at all — reaching 2019 is the point — so a count is the only honest bound left, and
 * the two flags are how the screen avoids stopping quietly.
 */
export interface EventSearchResults {
  /** What was searched for, trimmed. Lets a slow reply be matched to the query that asked it. */
  query: string;
  /** Still to come, soonest first. `endsAt >= now`, so one under way is here rather than in `past`. */
  upcoming: EventSummary[];
  /** Already over, most recent first. */
  past: EventSummary[];
  moreUpcoming: boolean;
  morePast: boolean;
  /**
   * A kind of event the query names, or null. **Never a result.** Kinds are deliberately not
   * searched — *show me the campouts* is a filter and belongs to TROOP-33 — so this is what keeps
   * typing one from reading as a unit that has never been on a campout. The empty state names it
   * back; nothing else uses it.
   */
  matchedKind: string | null;
}

/**
 * What the form sends.
 *
 * The four times are **local wall-clock strings** — exactly what a `datetime-local` input
 * produces, `2026-10-30T17:00`, with no zone and no `Z`. That is deliberate and it is the one
 * place this client does not send an instant: the browser must not be the thing that decides
 * which zone "5pm Friday" means, because the answer is the unit's zone and the browser is
 * frequently in a different one. The API resolves them and returns instants.
 */
export interface CreateEventInput {
  title: string;
  description?: string | null;
  locationName?: string | null;
  locationAddress?: string | null;
  startsAt: string;
  endsAt: string;
  departsAt?: string | null;
  returnsAt?: string | null;
  timeZoneId?: string | null;
  /** One of the unit's types, or null. Never required — see the picker in EventForm. */
  eventTypeId?: string | null;
  threadAudience?: 'Everyone' | 'AdultsOnly';
  /** A repeat, or absent for the ordinary single event. See `RepeatInput`. */
  repeat?: RepeatInput | null;
  /**
   * Which sections the event carries. Absent means "not stated", which the server answers from
   * the kind and then from its own default of on — so an older caller inherits the kind rather
   * than creating an event that asks nothing.
   */
  requiresRsvp?: boolean;
  requiresForms?: boolean;
  requiresPacking?: boolean;
  requiresTravel?: boolean;
  requiresProgram?: boolean;
}

export type RepeatCadence = 'EveryWeek' | 'EveryMonth';

export type RepeatWeek = 'First' | 'Second' | 'Third' | 'Fourth' | 'Last';

/**
 * A repeat, exactly as the server's options endpoint described it, plus the date to stop on.
 *
 * The weekday is absent because it comes from `startsAt` — a repeat is always the same weekday as
 * the event it repeats. `until` is a **date**, `2026-12-31`, with no time and therefore no zone:
 * "keep going until the end of term" is a date claim, and giving it a time would make where the
 * series ends depend on which zone resolved it.
 */
export interface RepeatInput {
  cadence: RepeatCadence;
  week?: RepeatWeek | null;
  until: string;
}

/**
 * One repeat a start date could have, and what it would come to.
 *
 * Asked for rather than worked out here. Which patterns describe a given date is a rule — the
 * fifth Wednesday of a month is only ever "the last one" — and a second copy of it in TypeScript
 * would part company with the server's the first time either was touched. `label` and
 * `lastDateLabel` arrive as words for the same reason, and for one more: `new Date('2026-12-30')`
 * is UTC midnight, which renders as the 29th everywhere west of Greenwich.
 */
export interface RepeatOption {
  cadence: RepeatCadence;
  week: RepeatWeek | null;
  /** "Every month on the last Wednesday". */
  label: string;
  /** How many events this would create, or null when it would not be allowed to. */
  count: number | null;
  /** "Wed 30 Dec 2026", or null alongside a null count. */
  lastDateLabel: string | null;
  /** Why this one cannot be picked, or null when it can. Never set alongside a count. */
  unavailable: string | null;
}

export interface CreatedEvent {
  eventId: string;
  conversationId: string;
  timeZoneId: string;
  /**
   * The other events a repeat created, first one excluded — that is `eventId`. Empty for an
   * ordinary create. Complete, independent events, not a handle on a series: there is no series.
   */
  alsoCreated: string[];
}

/**
 * What the edit form sends: the create shape without the thread audience, because who is in the
 * thread is not a property of the event.
 *
 * `timeZoneId` is not optional here the way it is on create. An edit resolves every reading again
 * from the wall-clock strings in the payload, so the zone they were read in has to travel with
 * them — omitting it on an event that carries an override would resolve "5pm at Philmont" in the
 * troop's own zone and move the whole trip.
 */
export interface UpdateEventInput {
  title: string;
  description?: string | null;
  locationName?: string | null;
  locationAddress?: string | null;
  startsAt: string;
  endsAt: string;
  departsAt?: string | null;
  returnsAt?: string | null;
  timeZoneId: string;
  eventTypeId?: string | null;
  /** Absent leaves each flag exactly as it was. */
  requiresRsvp?: boolean;
  requiresForms?: boolean;
  requiresPacking?: boolean;
  requiresTravel?: boolean;
  requiresProgram?: boolean;
}

export interface UpdatedEvent {
  eventId: string;
  timeZoneId: string;
}

export interface CancelledEvent {
  eventId: string;
  reason: string;
  cancelledAt: string;
  /** True when somebody else had already called it off. Still a success. */
  alreadyCancelled: boolean;
}

/** Field name -> messages, as ASP.NET's ValidationProblem returns them. */
export type FieldErrors = Record<string, string[]>;

export class ValidationFailure extends Error {
  constructor(public readonly errors: FieldErrors) {
    super('Some of these need another look.');
    this.name = 'ValidationFailure';
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; rule?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    // 400 carries per-field messages, so the form can put each one under its own box rather
    // than stacking a paragraph above everything.
    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    if (response.status === 409 && problem.title) {
      throw new ComplianceRefusal(problem.title, problem.detail ?? '', problem.rule);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * A parent's calendar. Outside `/units/` for the reason the whole `/family` group is: a guardian
 * holds no membership, so a unit-scoped route 404s the person it exists for. Their subscription
 * feed has listed these events all along (TROOP-66).
 *
 * Spans every unit their children are in, so each row carries its unit -- `Fall Camporee` alone is
 * ambiguous when both children are camping.
 */
export const familyEventsApi = {
  /**
   * What the family page opens on, across every unit their children are in — TROOP-213.
   *
   * The unit door's sibling, same as `calendar` below is, for the same rule-13b reason: a parent
   * should not have to learn a second version of this page.
   */
  agenda: () => call<EventAgendaPage>('/family/events/'),

  /**
   * The calendar a month at a time, across every unit their children are in — TROOP-13.
   *
   * The unit door's sibling and deliberately the same shape, because rule 13b's point is that
   * `/family` mirrors `/units` in routes and never in rules: same window, same payload, same
   * `MonthBar` on the screen. A parent should not have to learn a second calendar.
   */
  calendar: (anchor?: string) =>
    call<EventCalendarPage>(
      `/family/events/calendar${anchor ? `?anchor=${encodeURIComponent(anchor)}` : ''}`,
    ),

  /**
   * The same search across every unit their children are in — TROOP-214, and the unit door's
   * sibling for the same rule-13b reason: one rule, one component, two doors. Each row carries
   * its unit, because "Fall Camporee" alone is ambiguous when both children are camping.
   */
  search: (query: string) =>
    call<EventSearchResults>(`/family/events/search?q=${encodeURIComponent(query)}`),

  detail: (eventId: string) => call<EventDetail>(`/family/events/${eventId}`),

  /** Going / not going / maybe / no response, plus this family's own rows by name. */
  rsvps: (eventId: string) => call<RsvpSummary>(`/family/events/${eventId}/rsvps/`),

  respond: (eventId: string, personId: string, response: RsvpResponse, note?: string | null) =>
    call<{ personId: string; response: RsvpResponse }>(
      `/family/events/${eventId}/rsvps/${personId}`,
      { method: 'PUT', body: JSON.stringify({ response, note: note ?? null }) },
    ),
};

export type RsvpResponse = 'Going' | 'NotGoing' | 'Maybe';

export interface RsvpRow {
  personId: string;
  displayName: string;
  response: RsvpResponse;
  note: string | null;
  respondedByPersonId: string;
  respondedByName: string;
  respondedAt: string;
}

/**
 * Four numbers, and the fourth is the point: **no response is its own state**. Folding it into
 * `notGoing` is how a seat count comes out short -- silence is not a decline.
 */
export interface RsvpSummary {
  going: number;
  notGoing: number;
  maybe: number;
  noResponse: number;
  expected: number;
  /** Named rows the caller is entitled to. A parent gets their own family; nobody else's. */
  rows: RsvpRow[];
}

export const eventsApi = {
  /**
   * What the events page opens on: what is coming, then what has just finished — TROOP-213.
   *
   * This route *was* the upcoming list, and had been dead since the screens moved to `calendar`
   * below. Reshaped rather than left orphaned with a third one added beside it.
   */
  agenda: (unitId: string) => call<EventAgendaPage>(`/units/${unitId}/events/`),

  /**
   * The calendar a month at a time, walking backwards through the unit's history.
   *
   * Its own route rather than a shape `agenda` switches to: a response type that changes with a
   * query parameter is something a caller finds out about at runtime. It is what *View all*
   * reaches, unchanged — the agenda is a landing view beside it, not a replacement for it.
   */
  calendar: (unitId: string, anchor?: string) =>
    call<EventCalendarPage>(
      `/units/${unitId}/events/calendar${anchor ? `?anchor=${encodeURIComponent(anchor)}` : ''}`,
    ),

  /**
   * Finding one by typing at it — TROOP-214.
   *
   * Server-side, deliberately: `RosterPicker`'s *behaviour* is what this copies, not its
   * mechanism. That one fetches the whole roster once and filters in the browser, which is right
   * for ninety people and would mean shipping a decade of events here.
   */
  search: (unitId: string, query: string) =>
    call<EventSearchResults>(
      `/units/${unitId}/events/search?q=${encodeURIComponent(query)}`,
    ),

  detail: (unitId: string, eventId: string) =>
    call<EventDetail>(`/units/${unitId}/events/${eventId}`),

  /**
   * Who is coming, through the member's door.
   *
   * The route has existed since TROOP-46 and nothing called it until TROOP-116, so a Scout could
   * not answer for themselves on the one screen that asks, and a leader saw no roll-up at all —
   * the whole feature was reachable only by a guardian. `rows` arrives by name for adult officers
   * and empty for everybody else; that is the server's decision, not a thing to ask for.
   */
  rsvps: (unitId: string, eventId: string) =>
    call<RsvpSummary>(`/units/${unitId}/events/${eventId}/rsvps/`),

  /**
   * Answer for one person. Themselves always; an adult officer may also answer for anybody on the
   * roster, because a family telling a leader at the meeting is how half of these get recorded.
   * The row keeps who did it either way.
   */
  respond: (
    unitId: string,
    eventId: string,
    personId: string,
    response: RsvpResponse,
    note?: string | null,
  ) =>
    call<{ personId: string; response: RsvpResponse }>(
      `/units/${unitId}/events/${eventId}/rsvps/${personId}`,
      { method: 'PUT', body: JSON.stringify({ response, note: note ?? null }) },
    ),

  /**
   * What repeats are available for a start date, and what each would come to by `until`.
   *
   * A read that commits to nothing. `startsAt` is the same wall-clock string the form holds and
   * `until` is a bare date; both go over as typed, because resolving either in the browser is the
   * bug this whole area of the code exists to avoid.
   */
  repeatOptions: (unitId: string, startsAt: string, until: string) =>
    call<RepeatOption[]>(
      `/units/${unitId}/events/repeat-options?startsAt=${encodeURIComponent(startsAt)}` +
        `&until=${encodeURIComponent(until)}`,
    ),

  create: (unitId: string, input: CreateEventInput) =>
    call<CreatedEvent>(`/units/${unitId}/events/`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  update: (unitId: string, eventId: string, input: UpdateEventInput) =>
    call<UpdatedEvent>(`/units/${unitId}/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  /**
   * Calls the event off. Not a delete, and deliberately not `DELETE`: the event survives and
   * stays on the calendar, so the verb would be a lie about what the request does.
   */
  cancel: (unitId: string, eventId: string, reason: string) =>
    call<CancelledEvent>(`/units/${unitId}/events/${eventId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};
